import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import * as XLSX from "xlsx-js-style";
import {
  FiEdit2, FiTrash2, FiDownload, FiFilter,
  FiClock, FiDollarSign, FiCalendar, FiCheckCircle, FiChevronLeft, FiChevronRight, FiLoader,
  FiDatabase
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import timeTrackingService from '../../services/timeTrackingService';
import { CURRENCY_SYMBOL } from '../../config/constants';
import CommonModal from '../../services/CommonModal';
import { FaChevronDown, FaChevronRight, FaChevronUp } from 'react-icons/fa6';
import { customDateFormatShort } from '../../config/utils';
import { FaFileExcel } from 'react-icons/fa';

const TABS = [
  { key: 'unbilled', label: 'Milestones' },
  { key: 'draft', label: 'Draft' },
  { key: 'Pending', label: 'Pending' },
  { key: 'clarification', label: 'Clarification' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
];

const TABLE_TABS = ['draft', 'unbilled', 'Pending', 'clarification', 'approved', 'rejected'];

const payloadKeyMap = {
  draft: "time_entries_draft",
  unbilled: "milestones_closed",
  Pending: "time_entries",
  clarification: "time_entries",
  approved: "time_entries",
  rejected: "time_entries",
};

const priorityColors = {
  Low: 'badge badge-info',
  Medium: 'badge badge-warning',
  High: 'badge badge-danger',
  Urgent: 'badge badge-danger',
};

const getHoursColor = (estimated, completed) => {
  if (Math.abs(completed - estimated) < 0.01) {
    return "text-green-700 ";
  }

  if (completed < estimated) {
    return "text-orange-700 ";
  }

  return "text-red-700";
};

const TimeEntryList = ({ entries, onDelete, onRefresh, projects, tasks, onSubmitUnbilled }) => {
  const [filters, setFilters] = useState({
    project_id: '',
    task_id: '',
    start_date: '',
    end_date: '',
    status: '',
    is_billable: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [datePreset, setDatePreset] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTimeEntry, setSelectedTimeEntry] = useState(null);
  const [selectedApprovalStatus, setApprovalStatus] = useState('');
  const [activeTab, setActiveTab] = useState('unbilled');

  const [unbilledTasks, setUnbilledTasks] = useState([]);
  const [unbilledLoading, setUnbilledLoading] = useState(false);
  const [unbilledPagination, setUnbilledPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10 });
  const [draftTasks, setDraftTasks] = useState([]);
  const [pendingTasks, setPendingTasks] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftPagination, setDraftPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10 });

  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingPagination, setPendingPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10 });
  const [openRow, setOpenRow] = useState(null);

  const [approvedTasks, setApprovedTasks] = useState([]);
  const [rejectedTasks, setRejectedTasks] = useState([]);
  const [clarificationTasks, setClarificationTasks] = useState([]);

  const [approveLoading, setApprovedLoading] = useState(false);
  const [approvePagination, setApprovedPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10 });
  const [clarificationLoading, setClarificationLoading] = useState(false);
  const [clarificationPagination, setClarificationPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10 });
  const [rejectedLoading, setRejectedLoading] = useState(false);
  const [rejectedPagination, setRejectedPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10 });

  const [milestonesTasks, setMilestonesTasks] = useState([]);
  const [milestonesLoading, setMilestonesLoading] = useState(false);
  const [milestonesPagination, setMilestonesPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10 });
  const [selectedSprintIds, setSelectedSprintIds] = useState([]);
  const [openSubmission, setOpenSubmission] = useState(null);
  const [openMilestone, setOpenMilestone] = useState({});

  const [submissionTasks, setSubmissionTasks] = useState([]);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [submissionPagination, setSubmissionPagination] = useState({ total: 0, page: 1, pages: 1, limit: 10 });

  const toggleSubmission = (id) => {
    setOpenSubmission(openSubmission === id ? null : id);
  };

  const toggleMilestone = (id) => {
    setOpenMilestone((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  console.log("milestonesTasks===", milestonesTasks)

  const toggleRow = (id) => {
    setOpenRow(openRow === id ? null : id);
  };

  const confirmDelete = () => {
    onDelete(selectedTimeEntry.id);
    setShowDeleteModal(false);
    setSelectedTimeEntry(null);
  };
  const Inr = CURRENCY_SYMBOL

  useEffect(() => {
    if (entries && entries.length > 0) {
      calculateStatistics();
    }
  }, [entries]);

  const calculateStatistics = () => {
    const totalHours = entries.reduce((sum, entry) => sum + parseFloat(entry.hours_spent || 0), 0);
    const billableHours = entries
      .filter(entry => entry.is_billable)
      .reduce((sum, entry) => sum + parseFloat(entry.hours_spent || 0), 0);
    const totalRevenue = entries
      .filter(entry => entry.is_billable && entry.hourly_rate)
      .reduce((sum, entry) => sum + (parseFloat(entry.hours_spent || 0) * parseFloat(entry.hourly_rate || 0)), 0);

    setStatistics({
      totalHours: parseFloat(totalHours.toFixed(2)),
      billableHours: parseFloat(billableHours.toFixed(2)),
      nonBillableHours: parseFloat((totalHours - billableHours).toFixed(2)),
      totalRevenue: parseFloat(totalRevenue.toFixed(2)),
      totalEntries: entries.length
    });
  };





  const submitStatusChange = (e) => {
    const { name, value } = e.target;
    console.log("filterrr", name, value)
    setApprovalStatus(value)

  };

  // const fetchSubmissions = useCallback(async (tab, page = 1, project_id = '') => {
  //   const isDraft = tab === 'draft';
  //   const isPending = tab === 'Pending';
  //   const setLoading = isDraft ? setDraftLoading : isPending ? draftLoading : setUnbilledLoading;
  //   const setTasks = isDraft ? setDraftTasks : setUnbilledTasks;
  //   const setPagination = isDraft ? setDraftPagination : isPending ? setPendingPagination : setUnbilledPagination;
  //   const setTaskList =
  //     isDraft
  //       ? setDraftTasks
  //       : isPending
  //         ? setPendingTasks
  //         : setUnbilledTasks;

  //   setLoading(true);
  //   try {
  //     const res = await timeTrackingService.getUnbilledSubmissions({ page, project_id, status: tab });

  //     // const list = isDraft
  //     //   ? (res?.drafts || res?.data?.drafts || [])
  //     //   : (res?.unbilled || res?.data?.unbilled || res?.data || res || []);
  //     const setTaskList =
  //       isDraft
  //         ? setDraftTasks
  //         : isPending
  //           ? setPendingTasks
  //           : setUnbilledTasks;

  //     let list = [];

  //     if (tab === "draft") {
  //       list = res?.drafts || res?.data?.drafts || [];
  //     } else if (tab === "Pending") {
  //       list = res?.pending_entries || res?.data?.pending_entries || [];
  //     } else {
  //       list = res?.unbilled || res?.data?.unbilled || [];
  //     }

  //     const rawPagination =
  //       res?.pagination || res?.meta || res?.data?.pagination || res?.data?.meta || {};

  //     const limit = rawPagination.limit ?? rawPagination.per_page ?? rawPagination.pageSize ?? 10;
  //     const total = rawPagination.total ?? rawPagination.total_count ?? rawPagination.totalCount ?? list.length;
  //     const pages =
  //       rawPagination.pages ??
  //       rawPagination.total_pages ??
  //       rawPagination.totalPages ??
  //       Math.max(1, Math.ceil(total / (limit || 1)));
  //     const currentPage =
  //       rawPagination.page ?? rawPagination.current_page ?? rawPagination.currentPage ?? page;

  //     setTasks(list);
  //     setTaskList(list);
  //     setPagination({ total, page: currentPage, pages, limit });
  //   } catch (error) {
  //     toast.error(`Failed to load ${isDraft ? 'draft' : 'unbilled'} tasks`);
  //   } finally {
  //     setLoading(false);
  //   }
  // }, []);


  const fetchSubmissions = useCallback(async (tab, page = 1, project_id = '') => {
    const isDraft = tab === 'draft';
    const isPending = tab === 'Pending';
    const isApproved = tab === 'approved';
    const isClarification = tab === 'clarification';
    const isRejected = tab === 'rejected';

    let setLoading;
    let setTaskList;
    let setPagination;

    if (isDraft) {
      setLoading = setSubmissionLoading;
      setTaskList = setSubmissionTasks;
      setPagination = setSubmissionPagination;
    } else if (isPending) {
      setLoading = setPendingLoading;
      setTaskList = setPendingTasks;
      setPagination = setPendingPagination;
    } else if (isApproved) {
      setLoading = setApprovedLoading;
      setTaskList = setApprovedTasks;
      setPagination = setApprovedPagination;
    } else if (isClarification) {
      setLoading = setClarificationLoading;
      setTaskList = setClarificationTasks;
      setPagination = setClarificationPagination;

    } else if (isRejected) {
      setLoading = setRejectedLoading;
      setTaskList = setRejectedTasks;
      setPagination = setRejectedPagination;
    } else {
      setLoading = setMilestonesLoading;
      setTaskList = setMilestonesTasks;
      setPagination = setMilestonesPagination;
    }

    setLoading(true);

    try {
      const res = await timeTrackingService.getUnbilledSubmissions({
        page,
        project_id,
        status: tab,
      });

      let list = [];

      if (isDraft) {
        list = res?.drafts || res?.data?.drafts || [];
      } else if (isPending) {
        list = res?.pending_entries || res?.data?.pending_entries || [];
      } else if (isApproved) {
        list = res?.approved_entries || res?.data?.approved_entries || [];
      } else if (isRejected) {
        list = res?.rejected_entries || res?.data?.rejected_entries || [];
      } else if (isClarification) {
        list = res?.clarification_entries || res?.data?.clarification_entries || [];
      } else {
        list = res?.milestones_closed || res?.data?.milestones_closed || [];
      }

      setTaskList(list);
      setSubmissionTasks(res?.submissions || res?.data?.submissions || []);

      setPagination({
        total: res?.pagination?.total || list.length,
        page: res?.pagination?.page || page,
        pages: res?.pagination?.pages || 1,
        limit: res?.pagination?.limit || 10,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'unbilled' || activeTab === 'draft' || activeTab === 'Pending' || activeTab === 'approved' || activeTab === 'clarification' || activeTab === 'rejected') {
      fetchSubmissions(activeTab, 1, filters.project_id);
    }
  }, [activeTab, fetchSubmissions]);


  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    console.log("filterrr", name, value)
    setFilters(prev => ({ ...prev, [name]: value }));
    if (name === 'project_id' && (activeTab === 'unbilled' || activeTab === 'draft' || activeTab === 'Pending' || activeTab === 'approved' || activeTab === 'rejecetd' || activeTab === 'clarification')) {
      fetchSubmissions(activeTab, 1, value);
    }
  };

  const handleDatePresetChange = (preset) => {
    setDatePreset(preset);
    const presets = timeTrackingService.getDateRangePresets();
    if (presets[preset]) {
      setFilters(prev => ({
        ...prev,
        start_date: presets[preset].start_date,
        end_date: presets[preset].end_date
      }));
    }
  };

  const applyFilters = () => {
    onRefresh(filters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const emptyFilters = {
      project_id: '',
      task_id: '',
      start_date: '',
      end_date: '',
      status: '',
      is_billable: ''
    };
    setFilters(emptyFilters);
    setDatePreset('');
    onRefresh(emptyFilters);
    if (activeTab === 'unbilled' || activeTab === 'draft') {
      fetchSubmissions(activeTab, 1, '');
    }
  };

  const handleExport = () => {
    timeTrackingService.exportToCSV(entries);
    toast.success('Time entries exported successfully');
  };

  const handleDelete = async (id) => {
    {
      try {
        await onDelete(id);
        toast.success('Time entry deleted successfully');
      } catch (error) {
        toast.error('Failed to delete time entry');
      }
    }
  };


  const handleSelectSprintRow = (id) => {
    setSelectedSprintIds((prev) => {
      let updatedIds;

      if (prev.includes(id)) {
        updatedIds = prev.filter((item) => item !== id);
      } else {
        updatedIds = [...prev, id];
      }

      console.log("Selected Sprint IDs:", updatedIds);

      return updatedIds;
    });
  };

  const getStatusBadge = (status) => {
    console.log("status===", status)
    const statusColors = {
      draft: 'bg-gray-100 text-gray-800',
      submitted: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      done: 'bg-green-100 text-green-800',
      'to do': 'bg-gray-100 text-gray-800',
      'in progress': 'bg-blue-100 text-blue-800',
    };
    const key = (status || '').toLowerCase();
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[key] || statusColors.draft}`}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : '-'}
      </span>
    );
  };

  const getPriorityBadge = (priority) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${priorityColors[priority] || 'bg-gray-100 text-gray-700'}`}>
      {priority || '-'}
    </span>
  );

  const filteredTasks = tasks?.filter(task =>
    !filters.project_id || task.project_id === filters.project_id
  ) || [];


  const tabFilteredEntries = (entries || []).filter(entry => {
    if (activeTab === 'unbilled') {
      return !!entry.is_billable;
    }
    return entry.status === activeTab;
  });

  // Rows actually rendered for the active tab: task submissions for both
  // "unbilled" and "draft" (both come from the submission API), local
  const activeRows =
    activeTab === 'unbilled' ? milestonesTasks :
      activeTab === 'draft' ? draftTasks :
        tabFilteredEntries;

  const tabCounts = TABS.reduce((acc, tab) => {
    if (tab.key === 'unbilled') {
      acc[tab.key] = milestonesPagination.total;
    } else if (tab.key === 'draft') {
      acc[tab.key] = draftPagination.total;
    } else {
      acc[tab.key] = (entries || []).filter(entry => entry.status === tab.key).length;
    }
    return acc;
  }, {});
  const [selectedIds, setSelectedIds] = useState([]);

  const isAllSelected =
    activeRows.length > 0 &&
    activeRows.every(row => selectedIds.includes(row));

  const isAllSprintsSelected =
    activeRows.length > 0 &&
    activeRows.every(row => selectedSprintIds.includes(row.id));

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(activeRows.map(row => row));
    } else {
      setSelectedIds([]);
    }
  };


  const handleSprintSelectAll = (e) => {
    if (e.target.checked) {
      const allSprintIds = activeRows.map((row) => row.id);

      setSelectedSprintIds(allSprintIds);

      console.log("All selected sprint IDs:", allSprintIds);
    } else {
      setSelectedSprintIds([]);

      console.log("All sprint IDs cleared:", []);
    }
  };

  const handleSelectRow = (id) => {
    console.log("selected rows", id)
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(entryId => entryId !== id)
        : [...prev, id]
    );
    console.log("selectedIds", selectedIds)
  };
  useEffect(() => {
    console.log("selectedSprintIds updated:", selectedSprintIds);
    setSelectedIds([]);
  }, [activeTab, selectedSprintIds]);

  const handleSubmitSelected = async () => {
    const hasSelection =
      selectedIds.length > 0 || selectedSprintIds.length > 0;

    if (!hasSelection) {
      toast.error("Select at least one task first");
      return;
    }
    try {
      const payload = {
        [payloadKeyMap[activeTab] || "time_entries"]: selectedSprintIds,
        status: selectedApprovalStatus,
        project_id: filters.project_id,
      };

      console.log("idssssss", payload, activeTab)

      const resp = await timeTrackingService.submissionSprintEntry(payload);

      toast.success(resp.message);

      setSelectedIds([]);


      const page = activeTab === 'unbilled' ? unbilledPagination.page : draftPagination.page;
      fetchSubmissions(activeTab, page, filters.project_id);
    } catch (error) {
      toast.error('Failed to submit selected tasks');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return customDateFormatShort(dateString);
  };

  // excel sheet dwonloading
const handleDownloadMilestone = (obj) => {
  if (!obj || !obj.tasks || obj.tasks.length === 0) {
    toast.error("No tasks to download");
    return;
  }

  console.log("obj===", obj);

  // ==========================================
  // ROW 1 - MILESTONE HEADERS
  // ==========================================

  const milestoneHeaders = [
    "NAME",
    "START DATE",
    "END DATE",
    "STATUS",
    "ESTIMATED",
    "COMPLETED",
    "RATE",
    "AMOUNT",
  ];

  // ==========================================
  // CALCULATE TOTALS
  // ==========================================

  const totalEstimated = obj.tasks.reduce(
    (sum, task) =>
      sum + (Number(task.estimated_hours) || 0),
    0
  );

  const totalCompleted = obj.tasks.reduce(
    (sum, task) =>
      sum + (Number(task.actual_hours) || 0),
    0
  );

  const totalAmount = obj.tasks.reduce(
    (sum, task) => {
      const actual = Number(task.actual_hours) || 0;
      const rate = Number(task.billable_rate) || 0;

      return sum + actual * rate;
    },
    0
  );

  // ==========================================
  // ROW 2 - MILESTONE DETAILS
  // ==========================================

  const milestoneDetails = [
    obj.name ?? "",
    formatDate(obj.due_date),
    formatDate(obj.completion_date),
    obj.status ?? "",
    totalEstimated.toFixed(2),
    totalCompleted.toFixed(2),
    "",
    totalAmount.toFixed(2),
  ];

  // ==========================================
  // ROW 3 - TASK HEADERS
  // ==========================================

  const taskHeaders = [
    "TASK",
    "ESTIMATED",
    "COMPLETED",
    "RATE",
    "AMOUNT",
  ];

  // ==========================================
  // ROW 4+ - ALL TASKS
  // ==========================================

  const taskRows = obj.tasks.map((task) => {
    const actual = Number(task.actual_hours) || 0;
    const rate = Number(task.billable_rate) || 0;

    const amount = actual * rate;

    return [
      task.title ?? "",
      task.estimated_hours ?? "",
      task.actual_hours ?? "",
      task.billable_rate ?? "",
      amount.toFixed(2),
    ];
  });

  // ==========================================
  // LAST ROW - TOTAL
  // ==========================================

  const totalRow = [
    "TOTAL",
    totalEstimated.toFixed(2),
    totalCompleted.toFixed(2),
    "",
    totalAmount.toFixed(2),
  ];

  // ==========================================
  // COMPLETE EXCEL DATA
  // ==========================================

  const excelData = [
    milestoneHeaders,  // Row 1
    milestoneDetails,  // Row 2
    taskHeaders,       // Row 3
    ...taskRows,       // Row 4+
    totalRow,          // Last row
  ];

  // ==========================================
  // CREATE WORKSHEET
  // ==========================================

  const worksheet = XLSX.utils.aoa_to_sheet(excelData);

  // ==========================================
  // COLUMN WIDTHS
  // ==========================================

  worksheet["!cols"] = [
    { wch: 55 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
    { wch: 18 },
  ];

  // ==========================================
  // HEADER STYLE
  // ==========================================

  const headerStyle = {
    fill: {
      fgColor: {
        rgb: "1F4E78",
      },
    },
    font: {
      bold: true,
      color: {
        rgb: "FFFFFF",
      },
      sz: 11,
    },
    alignment: {
      horizontal: "center",
      vertical: "center",
    },
    border: {
      top: {
        style: "thin",
        color: {
          rgb: "FFFFFF",
        },
      },
      bottom: {
        style: "thin",
        color: {
          rgb: "FFFFFF",
        },
      },
      left: {
        style: "thin",
        color: {
          rgb: "FFFFFF",
        },
      },
      right: {
        style: "thin",
        color: {
          rgb: "FFFFFF",
        },
      },
    },
  };

  // ==========================================
  // MILESTONE DETAIL STYLE
  // ==========================================

  const milestoneStyle = {
    fill: {
      fgColor: {
        rgb: "EAF2F8",
      },
    },
    font: {
      bold: true,
      color: {
        rgb: "000000",
      },
    },
    alignment: {
      vertical: "center",
    },
  };

  // ==========================================
  // TOTAL STYLE
  // ==========================================

  const totalStyle = {
    fill: {
      fgColor: {
        rgb: "D9EAD3",
      },
    },
    font: {
      bold: true,
      color: {
        rgb: "000000",
        },
      sz: 11,
    },
    alignment: {
      vertical: "center",
    },
    border: {
      top: {
        style: "medium",
        color: {
          rgb: "70AD47",
        },
      },
      bottom: {
        style: "medium",
        color: {
          rgb: "70AD47",
        },
      },
    },
  };

  // ==========================================
  // ROW 1 - MILESTONE HEADER
  // ==========================================

  for (let col = 0; col < milestoneHeaders.length; col++) {
    const cell = XLSX.utils.encode_cell({
      r: 0,
      c: col,
    });

    if (worksheet[cell]) {
      worksheet[cell].s = headerStyle;
    }
  }

  // ==========================================
  // ROW 2 - MILESTONE DETAILS
  // ==========================================

  for (let col = 0; col < milestoneDetails.length; col++) {
    const cell = XLSX.utils.encode_cell({
      r: 1,
      c: col,
    });

    if (worksheet[cell]) {
      worksheet[cell].s = milestoneStyle;
    }
  }

  // ==========================================
  // ROW 3 - TASK HEADER
  // ==========================================

  for (let col = 0; col < taskHeaders.length; col++) {
    const cell = XLSX.utils.encode_cell({
      r: 2,
      c: col,
    });

    if (worksheet[cell]) {
      worksheet[cell].s = headerStyle;
    }
  }

  // ==========================================
  // TASK ROWS
  // ==========================================

  taskRows.forEach((_, index) => {
    const excelRowIndex = index + 3;

    for (let col = 0; col < 5; col++) {
      const cell = XLSX.utils.encode_cell({
        r: excelRowIndex,
        c: col,
      });

      if (worksheet[cell]) {
        worksheet[cell].s = {
          alignment: {
            vertical: "center",
            wrapText: true,
          },
          border: {
            bottom: {
              style: "thin",
              color: {
                rgb: "D9D9D9",
              },
            },
          },
        };
      }
    }
  });

  // ==========================================
  // LAST ROW - TOTAL
  // ==========================================

  const totalRowIndex = excelData.length - 1;

  for (let col = 0; col < totalRow.length; col++) {
    const cell = XLSX.utils.encode_cell({
      r: totalRowIndex,
      c: col,
    });

    if (worksheet[cell]) {
      worksheet[cell].s = totalStyle;
    }
  }

  // ==========================================
  // ROW HEIGHT
  // ==========================================

  worksheet["!rows"] = [
    { hpt: 25 }, // Row 1
    { hpt: 25 }, // Row 2
    { hpt: 25 }, // Row 3
  ];

  // ==========================================
  // CREATE WORKBOOK
  // ==========================================

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    "Milestone"
  );

  // ==========================================
  // DOWNLOAD EXCEL
  // ==========================================

  XLSX.writeFile(
    workbook,
    `${obj.name || "milestone"}.xlsx`
  );

  toast.success("Excel exported successfully");
};

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Hours</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.totalHours}</p>
              </div>
              <FiClock className="text-3xl text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Billable Hours</p>
                <p className="text-2xl font-bold text-green-600">{statistics.billableHours}</p>
              </div>
              <FiCheckCircle className="text-3xl text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600 d-flex"><span className="fs-2" style={{ fontSize: "1.5rem", marginTop: "0.5rem" }} ><Inr /></span>{statistics.totalRevenue.toFixed(2)}</p>
              </div>
              <Inr className="text-3xl text-green-500" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Entries</p>
                <p className="text-2xl font-bold text-gray-900">{statistics.totalEntries}</p>
              </div>
              <FiCalendar className="text-3xl text-purple-500" />
            </div>
          </div>
        </div>
      )}
      <CommonModal
        isOpen={showDeleteModal}
        title="Delete Time Entry"
        message="Are you sure you want to delete this time entry?"
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedTimeEntry(null);
        }}
        onConfirm={confirmDelete}
        type="error"
        confirmText="Delete"
        cancelText="Cancel"
      />
      {/* Filter and Export Bar */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            <FiFilter className="mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>

          <button
            onClick={handleExport}
            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            disabled={!entries || entries.length === 0}
          >
            <FiDownload className="mr-2" />
            Export CSV
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t space-y-4">
            {/* Date Presets */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Quick Date Range
              </label>
              <div className="flex flex-wrap gap-2">
                {['today', 'thisWeek', 'thisMonth', 'last7Days', 'last30Days'].map(preset => (
                  <button
                    key={preset}
                    onClick={() => handleDatePresetChange(preset)}
                    className={`px-3 py-1 rounded-lg text-sm transition ${datePreset === preset
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                  >
                    {preset.replace(/([A-Z])/g, ' $1').trim()}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project
                </label>
                <select
                  name="project_id"
                  value={filters.project_id}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Projects</option>
                  {projects?.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task
                </label>
                <select
                  name="task_id"
                  value={filters.task_id}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={!filters.project_id}
                >
                  <option value="">All Tasks</option>
                  {filteredTasks.map(task => (
                    <option key={task.id} value={task.id}>
                      {task.title}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Billable
                </label>
                <select
                  name="is_billable"
                  value={filters.is_billable}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All</option>
                  <option value="true">Billable Only</option>
                  <option value="false">Non-Billable Only</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={clearFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Clear Filters
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Status Tabs */}
      <div className="bg-white rounded-lg shadow px-4 pt-2">
        <div className="flex flex-wrap gap-1 border-b border-gray-200">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-4 py-2 text-sm font-medium transition border-b-2 -mb-px ${activeTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>


      <div className='form-group w-1/4'>
        {/* <label className="block text-sm font-medium text-gray-700 mb-1">
          Project
        </label> */}
        <select
          name="project_id"
          value={filters.project_id}
          onChange={handleFilterChange}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="">All Projects</option>
          {projects?.map(project => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
      </div>

      {/* Time Entries / Unbilled Tasks Table */}
      {TABLE_TABS.includes(activeTab) ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">

            {activeTab === 'unbilled' ? (
              <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                        <input
                          type="checkbox"
                          checked={isAllSprintsSelected}
                          onChange={handleSprintSelectAll}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Start Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        End Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Status
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Estimated
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Completed
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Amount
                      </th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>

                  <tbody>
                    {milestonesTasks.length > 0 ? (
                      milestonesTasks.map((project) => (
                        <>
                          {/* Main Row */}
                          <tr
                            key={project.id}
                            className={`border-t hover:bg-gray-50 cursor-pointer ${selectedSprintIds.includes(project.id) ? "bg-blue-50" : ""
                              }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedSprintIds.includes(project.id)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectSprintRow(project.id);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>

                            <td
                              className="px-6 py-4 font-medium cursor-pointer"
                              onClick={() => toggleRow(project.id)}
                            >
                              {project.name}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {customDateFormatShort(project.due_date)}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {customDateFormatShort(project.completion_date)}
                            </td>

                            {/* Status */}
                            <td onClick={() => toggleRow(project.id)}>
                              <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {project.status === "submitted"
                                  ? "Approval pending"
                                  : project.status}
                              </span>
                            </td>

                            {/* Total Estimated */}
                            <td
                              className="px-4 py-4 text-sm font-medium"
                              onClick={() => toggleRow(project.id)}
                            >
                              {project?.tasks
                                ?.reduce(
                                  (sum, task) => sum + (Number(task?.estimated_hours) || 0),
                                  0
                                )
                                .toFixed(2)}
                            </td>

                            {/* Total Completed */}
                            <td
                              className="px-4 py-4 text-sm font-medium"
                              onClick={() => toggleRow(project.id)}
                            >
                              {project?.tasks
                                ?.reduce(
                                  (sum, task) => sum + (Number(task?.actual_hours) || 0),
                                  0
                                )
                                .toFixed(2)}
                            </td>

                            {/* Total Rate */}
                            <td
                              className="px-4 py-4 text-sm font-medium"
                              onClick={() => toggleRow(project.id)}
                            >
                              {project?.tasks
                                ?.reduce(
                                  (sum, task) => sum + (Number(task?.billable_rate) || 0),
                                  0
                                )
                                .toFixed(2)}
                            </td>

                            {/* Total Amount */}
                            <td
                              className="px-4 py-4 text-sm font-semibold text-green-600"
                              onClick={() => toggleRow(project.id)}
                            >

                              <span className='flex'> <Inr /> {project?.tasks
                                ?.reduce(
                                  (sum, task) =>
                                    sum +
                                    (Number(task?.actual_hours) || 0) *
                                    (Number(task?.billable_rate) || 0),
                                  0
                                )
                                .toFixed(2)} </span>
                            </td>

                            {/* Expand */}
                            <td
                              className="px-4 cursor-pointer flex"
                            >
                              <button
                                onClick={() => handleDownloadMilestone(project)}
                                className="mt-3 mr-3 px-3 py-2 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
                                title="Download list"
                              >
                                <FaFileExcel />
                              </button>

                              <span className='mt-5' onClick={() => toggleRow(project.id)}>
                                {openRow === project.id ? (
                                  <FaChevronUp size={18} />
                                ) : (
                                  <FaChevronDown size={18} />
                                )}
                              </span>

                            </td>
                          </tr>

                          {/* Accordion Row */}
                          {openRow === project.id && (
                            <tr>
                              <td colSpan={12} className="bg-gray-50 p-4">
                                <div className="overflow-x-auto">
                                  {(() => {
                                    const totalEstimatedHours = project?.tasks?.reduce(
                                      (sum, entry) => sum + (Number(entry?.estimated_hours) || 0),
                                      0
                                    );

                                    const totalActualHours = project?.tasks?.reduce(
                                      (sum, entry) => sum + (Number(entry?.actual_hours) || 0),
                                      0
                                    );

                                    const totalAmount = project?.tasks?.reduce(
                                      (sum, entry) =>
                                        sum +
                                        ((Number(entry?.actual_hours) || 0) *
                                          (Number(entry?.billable_rate) || 0)),
                                      0
                                    );


                                    const totalDuration = project?.tasks?.reduce(
                                      (sum, entry) => sum + (Number(entry.duration) || 0),
                                      0
                                    );

                                    const totalRate = project?.tasks?.reduce(
                                      (sum, entry) => sum + (Number(entry.billable_rate) || 0),
                                      0
                                    );

                                    return (
                                      <table className="min-w-full border border-gray-200 rounded-lg">
                                        <thead className="bg-gray-100">
                                          <tr>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Task
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Estimated
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Completed
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Rate
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Amount
                                            </th>
                                          </tr>
                                        </thead>

                                        <tbody className="bg-white">
                                          {project?.tasks?.map((entry) => {
                                            const amount =
                                              (Number(entry?.actual_hours) || 0) *
                                              (Number(entry?.billable_rate) || 0);

                                            return (
                                              <tr key={entry.id}>
                                                <td className="px-4 py-2 border">
                                                  {entry?.title || "-"}
                                                </td>


                                                <td className="px-4 py-2 border">
                                                  {entry?.estimated_hours || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.actual_hours || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.billable_rate || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {amount.toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}

                                          {/* Total Row */}
                                          <tr className="bg-gray-100 font-semibold">
                                            <td className="px-4 py-2 border text-right">
                                              Total
                                            </td>

                                            <td className="px-4 py-2 border">
                                              {totalEstimatedHours.toFixed(2) || 0}
                                            </td>

                                            <td className="px-4 py-2 border">{totalActualHours.toFixed(2) || 0}</td>

                                            <td className="px-4 py-2 border">{totalRate.toFixed(2) || 0.00}</td>

                                            <td className="px-4 py-2 border">
                                              {totalAmount.toFixed(2) || 0.00}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    );
                                  })()}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))) : (
                      <tr>
                        <td colSpan={7} className="py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <FiDatabase className="text-4xl mb-2 text-gray-400" />
                            <p className="text-sm font-medium">No pending tasks found.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            ) : activeTab === 'Pending' ? (
              <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Submitted on
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Comments
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">

                      </th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>

                  <tbody>
                    {pendingTasks.length > 0 ? (
                      pendingTasks.map((project) => (
                        <>
                          {/* Main Row */}
                          <tr
                            key={project.id}
                            className={`border-t hover:bg-gray-50 cursor-pointer ${selectedIds.includes(project) ? "bg-blue-50" : ""
                              }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(project)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectRow(project);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>

                            <td
                              className="px-6 py-4 font-medium cursor-pointer"
                              onClick={() => toggleRow(project.id)}
                            >
                              {project.submission_code}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {customDateFormatShort(project.submitted_date)}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800`}>{project.status === 'submitted' ? 'Approval pending' : `${project.status}`}</span>
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {project.comments || "-"}
                            </td>

                            <td
                              className="px-4 cursor-pointer"
                              onClick={() => toggleRow(project.id)}
                            >
                              {openRow === project.id ? (
                                <FaChevronUp size={18} />
                              ) : (
                                <FaChevronDown size={18} />
                              )}
                            </td>
                          </tr>

                          {/* Accordion Row */}
                          {openRow === project.id && (
                            <tr>
                              <td colSpan={5} className="bg-gray-50 p-4">
                                <div className="overflow-x-auto">
                                  {(() => {
                                    const totalHours = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry?.hours_spent) || 0),
                                      0
                                    );

                                    const totalAmount = project?.submission_time_entries?.reduce(
                                      (sum, entry) =>
                                        sum +
                                        ((Number(entry?.hours_spent) || 0) *
                                          (Number(entry?.hourly_rate) || 0)),
                                      0
                                    );


                                    const totalDuration = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry.duration) || 0),
                                      0
                                    );

                                    const totalRate = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry.hourly_rate) || 0),
                                      0
                                    );

                                    return (
                                      <table className="min-w-full border border-gray-200 rounded-lg">
                                        <thead className="bg-gray-100">
                                          <tr>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Task
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Hours
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Duration
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Rate
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Amount
                                            </th>
                                          </tr>
                                        </thead>

                                        <tbody className="bg-white">
                                          {project?.submission_time_entries?.map((entry) => {
                                            const amount =
                                              (Number(entry?.hours_spent) || 0) *
                                              (Number(entry?.hourly_rate) || 0);

                                            return (
                                              <tr key={entry.id}>
                                                <td className="px-4 py-2 border">
                                                  {entry?.task?.title || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.hours_spent || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.duration || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.hourly_rate || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {amount.toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}

                                          {/* Total Row */}
                                          <tr className="bg-gray-100 font-semibold">
                                            <td className="px-4 py-2 border text-right">
                                              Total
                                            </td>

                                            {/* <td className="px-4 py-2 border">
                                              {totalHours.toFixed(2) || 0.00}
                                            </td> */}

                                            <td className="px-4 py-2 border">{totalDuration.toFixed(2) || 0.00}</td>

                                            <td className="px-4 py-2 border">{totalRate.toFixed(2) || 0.00}</td>

                                            <td className="px-4 py-2 border">
                                              {totalAmount.toFixed(2) || 0.00}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    );
                                  })()}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))) : (
                      <tr>
                        <td colSpan={7} className="py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <FiDatabase className="text-4xl mb-2 text-gray-400" />
                            <p className="text-sm font-medium">No pending tasks found.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

            ) : activeTab === 'clarification' ? (
              <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Submitted on
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Comments
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">

                      </th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>

                  <tbody>
                    {clarificationTasks.length > 0 ? (
                      clarificationTasks.map((project) => (
                        <>
                          {/* Main Row */}
                          <tr
                            key={project.id}
                            className={`border-t hover:bg-gray-50 cursor-pointer ${selectedIds.includes(project) ? "bg-blue-50" : ""
                              }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(project)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectRow(project);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>

                            <td
                              className="px-6 py-4 font-medium cursor-pointer"
                              onClick={() => toggleRow(project.id)}
                            >
                              {project.submission_code}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {customDateFormatShort(project.submitted_date)}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800`}>{project.status === 'submitted' ? 'Approval pending' : `${project.status}`}</span>
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {project.comments || "-"}
                            </td>

                            <td
                              className="px-4 cursor-pointer"
                              onClick={() => toggleRow(project.id)}
                            >
                              {openRow === project.id ? (
                                <FaChevronUp size={18} />
                              ) : (
                                <FaChevronDown size={18} />
                              )}
                            </td>
                          </tr>

                          {/* Accordion Row */}
                          {openRow === project.id && (
                            <tr>
                              <td colSpan={5} className="bg-gray-50 p-4">
                                <div className="overflow-x-auto">
                                  {(() => {
                                    const totalHours = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry?.hours_spent) || 0),
                                      0
                                    );

                                    const totalAmount = project?.submission_time_entries?.reduce(
                                      (sum, entry) =>
                                        sum +
                                        ((Number(entry?.hours_spent) || 0) *
                                          (Number(entry?.hourly_rate) || 0)),
                                      0
                                    );


                                    const totalDuration = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry.duration) || 0),
                                      0
                                    );

                                    const totalRate = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry.hourly_rate) || 0),
                                      0
                                    );

                                    return (
                                      <table className="min-w-full border border-gray-200 rounded-lg">
                                        <thead className="bg-gray-100">
                                          <tr>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Task
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Hours
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Duration
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Rate
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Amount
                                            </th>
                                          </tr>
                                        </thead>

                                        <tbody className="bg-white">
                                          {project?.submission_time_entries?.map((entry) => {
                                            const amount =
                                              (Number(entry?.hours_spent) || 0) *
                                              (Number(entry?.hourly_rate) || 0);

                                            return (
                                              <tr key={entry.id}>
                                                <td className="px-4 py-2 border">
                                                  {entry?.task?.title || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.hours_spent || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.duration || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.hourly_rate || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {amount.toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}

                                          {/* Total Row */}
                                          <tr className="bg-gray-100 font-semibold">
                                            <td className="px-4 py-2 border text-right">
                                              Total
                                            </td>

                                            <td className="px-4 py-2 border">
                                              {totalHours.toFixed(2) || 0.00}
                                            </td>

                                            <td className="px-4 py-2 border">{totalDuration.toFixed(2) || 0.00}</td>

                                            <td className="px-4 py-2 border">{totalRate.toFixed(2) || 0.00}</td>

                                            <td className="px-4 py-2 border">
                                              {totalAmount.toFixed(2) || 0.00}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    );
                                  })()}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))) : (
                      <tr>
                        <td colSpan={7} className="py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <FiDatabase className="text-4xl mb-2 text-gray-400" />
                            <p className="text-sm font-medium">No clarification tasks found.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

            ) : activeTab === 'approved' ? (
              <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Submitted on
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Comments
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">

                      </th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>

                  <tbody>
                    {approvedTasks.length > 0 ? (
                      approvedTasks.map((project) => (
                        <>
                          {/* Main Row */}
                          <tr
                            key={project.id}
                            className={`border-t hover:bg-gray-50 cursor-pointer ${selectedIds.includes(project) ? "bg-blue-50" : ""
                              }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(project)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectRow(project);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>

                            <td
                              className="px-6 py-4 font-medium cursor-pointer"
                              onClick={() => toggleRow(project.id)}
                            >
                              {project.submission_code}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {customDateFormatShort(project.submitted_date)}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800`}>{project.status === 'submitted' ? 'Approval pending' : `${project.status}`}</span>
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {project.comments || "-"}
                            </td>

                            <td
                              className="px-4 cursor-pointer"
                              onClick={() => toggleRow(project.id)}
                            >
                              {openRow === project.id ? (
                                <FaChevronUp size={18} />
                              ) : (
                                <FaChevronDown size={18} />
                              )}
                            </td>
                          </tr>

                          {/* Accordion Row */}
                          {openRow === project.id && (
                            <tr>
                              <td colSpan={5} className="bg-gray-50 p-4">
                                <div className="overflow-x-auto">
                                  {(() => {
                                    const totalHours = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry?.hours_spent) || 0),
                                      0
                                    );

                                    const totalAmount = project?.submission_time_entries?.reduce(
                                      (sum, entry) =>
                                        sum +
                                        ((Number(entry?.hours_spent) || 0) *
                                          (Number(entry?.hourly_rate) || 0)),
                                      0
                                    );


                                    const totalDuration = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry.duration) || 0),
                                      0
                                    );

                                    const totalRate = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry.hourly_rate) || 0),
                                      0
                                    );

                                    return (
                                      <table className="min-w-full border border-gray-200 rounded-lg">
                                        <thead className="bg-gray-100">
                                          <tr>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Task
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Hours
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Duration
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Rate
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Amount
                                            </th>
                                          </tr>
                                        </thead>

                                        <tbody className="bg-white">
                                          {project?.submission_time_entries?.map((entry) => {
                                            const amount =
                                              (Number(entry?.hours_spent) || 0) *
                                              (Number(entry?.hourly_rate) || 0);

                                            return (
                                              <tr key={entry.id}>
                                                <td className="px-4 py-2 border">
                                                  {entry?.task?.title || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.hours_spent || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.duration || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.hourly_rate || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {amount.toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}

                                          {/* Total Row */}
                                          <tr className="bg-gray-100 font-semibold">
                                            <td className="px-4 py-2 border text-right">
                                              Total
                                            </td>

                                            <td className="px-4 py-2 border">
                                              {totalHours.toFixed(2) || 0.00}
                                            </td>

                                            <td className="px-4 py-2 border">{totalDuration.toFixed(2) || 0.00}</td>

                                            <td className="px-4 py-2 border">{totalRate.toFixed(2) || 0.00}</td>

                                            <td className="px-4 py-2 border">
                                              {totalAmount.toFixed(2) || 0.00}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    );
                                  })()}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))) : (
                      <tr>
                        <td colSpan={7} className="py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <FiDatabase className="text-4xl mb-2 text-gray-400" />
                            <p className="text-sm font-medium">No approved tasks found.</p>
                          </div>
                        </td>
                      </tr>
                    )}

                  </tbody>
                </table>
              </div>

            ) : activeTab === 'rejected' ? (
              <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                <table className="min-w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Code
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Submitted on
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">
                        Comments
                      </th>

                      <th className="px-6 py-3 text-left text-xs font-semibold uppercase">

                      </th>
                      <th className="w-12"></th>
                    </tr>
                  </thead>

                  <tbody>
                    {rejectedTasks.length > 0 ? (
                      rejectedTasks.map((project) => (
                        <>
                          {/* Main Row */}
                          <tr
                            key={project.id}
                            className={`border-t hover:bg-gray-50 cursor-pointer ${selectedIds.includes(project) ? "bg-blue-50" : ""
                              }`}
                          >
                            <td className="px-6 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(project)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleSelectRow(project);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>

                            <td
                              className="px-6 py-4 font-medium cursor-pointer"
                              onClick={() => toggleRow(project.id)}
                            >
                              {project.submission_code}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {customDateFormatShort(project.submitted_date)}
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800`}>{project.status === 'submitted' ? 'Approval pending' : `${project.status}`}</span>
                            </td>

                            <td onClick={() => toggleRow(project.id)}>
                              {project.comments || "-"}
                            </td>

                            <td
                              className="px-4 cursor-pointer"
                              onClick={() => toggleRow(project.id)}
                            >
                              {openRow === project.id ? (
                                <FaChevronUp size={18} />
                              ) : (
                                <FaChevronDown size={18} />
                              )}
                            </td>
                          </tr>

                          {/* Accordion Row */}
                          {openRow === project.id && (
                            <tr>
                              <td colSpan={5} className="bg-gray-50 p-4">
                                <div className="overflow-x-auto">
                                  {(() => {
                                    const totalHours = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry?.hours_spent) || 0),
                                      0
                                    );

                                    const totalAmount = project?.submission_time_entries?.reduce(
                                      (sum, entry) =>
                                        sum +
                                        ((Number(entry?.hours_spent) || 0) *
                                          (Number(entry?.hourly_rate) || 0)),
                                      0
                                    );


                                    const totalDuration = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry.duration) || 0),
                                      0
                                    );

                                    const totalRate = project?.submission_time_entries?.reduce(
                                      (sum, entry) => sum + (Number(entry.hourly_rate) || 0),
                                      0
                                    );

                                    return (
                                      <table className="min-w-full border border-gray-200 rounded-lg">
                                        <thead className="bg-gray-100">
                                          <tr>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Task
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Hours
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Duration
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Rate
                                            </th>
                                            <th className="px-4 py-2 text-left text-xs font-semibold uppercase border">
                                              Amount
                                            </th>
                                          </tr>
                                        </thead>

                                        <tbody className="bg-white">
                                          {project?.submission_time_entries?.map((entry) => {
                                            const amount =
                                              (Number(entry?.hours_spent) || 0) *
                                              (Number(entry?.hourly_rate) || 0);

                                            return (
                                              <tr key={entry.id}>
                                                <td className="px-4 py-2 border">
                                                  {entry?.task?.title || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.hours_spent || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.duration || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {entry?.hourly_rate || "-"}
                                                </td>

                                                <td className="px-4 py-2 border">
                                                  {amount.toFixed(2)}
                                                </td>
                                              </tr>
                                            );
                                          })}

                                          {/* Total Row */}
                                          <tr className="bg-gray-100 font-semibold">
                                            <td className="px-4 py-2 border text-right">
                                              Total
                                            </td>

                                            <td className="px-4 py-2 border">
                                              {totalHours.toFixed(2) || 0.00}
                                            </td>

                                            <td className="px-4 py-2 border">{totalDuration.toFixed(2) || 0.00}</td>

                                            <td className="px-4 py-2 border">{totalRate.toFixed(2) || 0.00}</td>

                                            <td className="px-4 py-2 border">
                                              {totalAmount.toFixed(2) || 0.00}
                                            </td>
                                          </tr>
                                        </tbody>
                                      </table>
                                    );
                                  })()}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))) : (
                      <tr>
                        <td colSpan={7} className="py-10">
                          <div className="flex flex-col items-center justify-center text-gray-500">
                            <FiDatabase className="text-4xl mb-2 text-gray-400" />
                            <p className="text-sm font-medium">No rejected tasks found.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

            )
              : (
                // ---- DRAFT TAB: time-entry data shape ----
                <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
                  <table className="min-w-full">
                    {/* Main Header */}
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left">Submission Code</th>
                        <th className="px-4 py-3 text-left">Estimated Hours</th>
                        <th className="px-4 py-3 text-left">Completed Hours</th>
                        <th className="px-4 py-3 text-left">Amount</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left w-10"></th>

                      </tr>
                    </thead>

                    <tbody>
                      {submissionTasks?.map((submission) => (


                        <React.Fragment key={submission.id}>

                          {/* Submission Row */}
                          <tr
                            className="bg-gray-100 hover:bg-gray-200 cursor-pointer border-b"
                            onClick={() => toggleSubmission(submission.id)}
                          >

                            <td className="px-4 py-4 font-semibold">
                              {submission.submission_code}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {submission.milestones?.reduce(
                                (total, milestone) =>
                                  total +
                                  (milestone.time_entries?.reduce(
                                    (sum, entry) => sum + (Number(entry.task?.estimated_hours) || 0),
                                    0
                                  ) || 0),
                                0
                              )}
                            </td>

                            <td className="px-4 py-3 text-center">
                              {submission.milestones?.reduce(
                                (total, milestone) =>
                                  total +
                                  (milestone.time_entries?.reduce(
                                    (sum, entry) => sum + (Number(entry.task?.actual_hours) || 0),
                                    0
                                  ) || 0),
                                0
                              )}
                            </td>

                            <td className="px-4 py-3 font-semibold text-green-600">
                              ₹
                              {(
                                submission.milestones?.reduce((milestoneTotal, milestone) => {
                                  return (
                                    milestoneTotal +
                                    (milestone.time_entries?.reduce((taskTotal, entry) => {
                                      return (
                                        taskTotal +
                                        (Number(entry.task?.actual_hours) || 0) *
                                        (Number(entry.task?.billable_rate) || 0)
                                      );
                                    }, 0) || 0)
                                  );
                                }, 0) || 0
                              ).toFixed(2)}
                            </td>
                            <td className="px-4 py-4">
                              <span className="px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs">
                                {submission.status}
                              </span>
                            </td>
                            <td className="px-4 py-4">
                              {openSubmission === submission.id ? (
                                <FaChevronUp />
                              ) : (
                                <FaChevronDown />
                              )}
                            </td>
                          </tr>

                          {/* Milestones */}
                          {openSubmission === submission.id && (

                            <tr>
                              <td colSpan={7} className="p-0">

                                <table className="w-full border border-gray-200">
                                  <thead className="bg-blue-100">
                                    <tr>
                                      <th className="px-4 py-3 text-left">Milestone</th>
                                      <th className="px-4 py-3 text-left">Due Date</th>
                                      <th className="px-4 py-3 text-left">Completion Date</th>
                                      <th className="px-4 py-3 text-right">Total</th>
                                      <th className="w-12 px-4 py-3"></th>
                                    </tr>
                                  </thead>

                                  <tbody>

                                    {submission.milestones?.map((milestone) => {
                                      const sprintTotal = milestone.time_entries?.reduce((sum, entry) => {
                                        const actual = Number(entry.task?.actual_hours) || 0;
                                        const rate = Number(entry.task?.billable_rate) || 0;
                                        return sum + actual * rate;
                                      }, 0) || 0;

                                      return (

                                        <React.Fragment key={milestone.id}>

                                          {/* Milestone Row */}
                                          <tr
                                            className="bg-white border-b hover:bg-gray-50 cursor-pointer"
                                            onClick={() => toggleMilestone(milestone.id)}
                                          >

                                            <td className="px-4 py-3 font-semibold text-blue-700">
                                              {milestone.name}
                                            </td>

                                            <td className="px-4 py-3">
                                              {milestone.due_date || "--"}
                                            </td>

                                            <td className="px-4 py-3">
                                              {milestone.completion_date || "--"}
                                            </td>

                                            <td className="px-4 py-3">
                                              {openMilestone[milestone.id] ? (
                                                < FaChevronUp />
                                              ) : (
                                                <  FaChevronDown />
                                              )}
                                            </td>
                                          </tr>

                                          {/* Tasks Table */}
                                          {openMilestone[milestone.id] && (
                                            <tr>
                                              <td colSpan={5} className="p-0 bg-gray-50">

                                                <table className="w-full border-t border-gray-200">
                                                  <thead className="bg-gray-100">
                                                    <tr>
                                                      <th className="px-3 py-3 text-left">Task Code</th>
                                                      <th className="px-3 py-3 text-left">Task Title</th>
                                                      <th className="px-3 py-3 text-left">Estimated</th>
                                                      <th className="px-3 py-3 text-left">Completed</th>
                                                      <th className="px-3 py-3 text-left">Rate</th>
                                                      <th className="px-3 py-3 text-left">Amount</th>
                                                    </tr>
                                                  </thead>

                                                  <tbody>
                                                    {milestone.time_entries?.length > 0 ? (
                                                      <>
                                                        {milestone.time_entries.map((entry) => {
                                                          const estimated = Number(entry.task?.estimated_hours) || 0;
                                                          const actual = Number(entry.task?.actual_hours) || 0;
                                                          const rate = Number(entry.task?.billable_rate) || 0;
                                                          const amount = actual * rate;

                                                          return (
                                                            <tr key={entry.id} className="border-t hover:bg-white">
                                                              <td className="px-3 py-3">
                                                                {entry.task?.task_code || "--"}
                                                              </td>

                                                              <td className="px-3 py-3">
                                                                {entry.task?.title || entry.task?.task_title || "--"}
                                                              </td>

                                                              <td className="px-3 py-3">{estimated}</td>

                                                              <td
                                                                className={`px-3 py-3 font-medium ${getHoursColor(
                                                                  estimated,
                                                                  actual
                                                                )}`}
                                                              >
                                                                {actual.toFixed(2)}
                                                              </td>

                                                              <td className="px-3 py-3">₹{rate}</td>

                                                              <td className="px-3 py-3 font-medium">
                                                                ₹{amount.toFixed(2)}
                                                              </td>
                                                            </tr>
                                                          );
                                                        })}

                                                        {/* Total Row */}
                                                        <tr className="bg-gray-100 font-bold border-t-2">
                                                          <td colSpan={2} className="px-3 py-3 text-right">
                                                            Total
                                                          </td>

                                                          <td className="px-3 py-3">
                                                            {milestone.time_entries.reduce(
                                                              (sum, entry) =>
                                                                sum + (Number(entry.task?.estimated_hours) || 0),
                                                              0
                                                            )}
                                                          </td>

                                                          <td className="px-3 py-3">
                                                            {milestone.time_entries.reduce(
                                                              (sum, entry) =>
                                                                sum + (Number(entry.task?.actual_hours) || 0),
                                                              0
                                                            )}
                                                          </td>

                                                          <td className="px-3 py-3">
                                                            ₹
                                                            {milestone.time_entries.reduce(
                                                              (sum, entry) =>
                                                                sum + (Number(entry.task?.billable_rate) || 0),
                                                              0
                                                            )}
                                                          </td>

                                                          <td className="px-3 py-3">
                                                            ₹
                                                            {milestone.time_entries
                                                              .reduce((sum, entry) => {
                                                                const actual = Number(entry.task?.actual_hours) || 0;
                                                                const rate = Number(entry.task?.billable_rate) || 0;
                                                                return sum + actual * rate;
                                                              }, 0)
                                                              .toFixed(2)}
                                                          </td>
                                                        </tr>
                                                      </>
                                                    ) : (
                                                      <tr>
                                                        <td colSpan={6} className="text-center py-4">
                                                          No Tasks Found
                                                        </td>
                                                      </tr>
                                                    )}
                                                  </tbody>
                                                </table>

                                              </td>
                                            </tr>
                                          )}

                                        </React.Fragment>
                                      )
                                    })}
                                  </tbody>
                                </table>

                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}


            {(activeTab === 'unbilled' || activeTab === 'draft' || activeTab === 'Pending' || activeTab === 'approved' || activeTab === 'rejected' || activeTab === 'clarification') && (() => {
              // const pag = activeTab === 'unbilled' ? unbilledPagination : draftPagination;
              // const loading = activeTab === 'unbilled' ? unbilledLoading : draftLoading;

              const pag =
                activeTab === 'unbilled'
                  ? unbilledPagination
                  : activeTab === 'draft'
                    ? submissionPagination
                    : activeTab === 'Pending'
                      ? pendingPagination
                      : activeTab === 'approved'
                        ? approvePagination
                        : activeTab === 'rejected'
                          ? rejectedPagination
                          : activeTab === 'clarification'
                            ? clarificationPagination
                            : { total: 0, page: 1, pages: 1, limit: 10 };

              const loading =
                activeTab === 'unbilled'
                  ? unbilledLoading
                  : activeTab === 'draft'
                    ? submissionLoading
                    : activeTab === 'Pending'
                      ? pendingLoading
                      : activeTab === 'approved'
                        ? approveLoading
                        : activeTab === 'rejected'
                          ? rejectedLoading
                          : activeTab === 'clarification'
                            ? clarificationLoading
                            : false;
              return (
                <div className="px-6 py-3 border-b flex items-center justify-between">
                  {/* <span className="text-sm text-gray-500">
                    {pag.total} total {pag.total === 1 ? 'record' : 'records'}
                    {pag.pages > 1 && (
                      <> &middot; Page {pag.page} of {pag.pages}</>
                    )}
                  </span> */}
                  {pag.pages > 1 && (
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => fetchSubmissions(activeTab, pag.page - 1, filters.project_id)}
                        disabled={pag.page <= 1 || loading}
                        className="p-2 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40 hover:bg-gray-50 transition"
                      >
                        <FiChevronLeft />
                      </button>
                      <button
                        onClick={() => fetchSubmissions(activeTab, pag.page + 1, filters.project_id)}
                        disabled={pag.page >= pag.pages || loading}
                        className="p-2 rounded-lg border border-gray-300 text-gray-600 disabled:opacity-40 hover:bg-gray-50 transition"
                      >
                        <FiChevronRight />
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}

            {(activeTab === "unbilled" || activeTab === "draft") && (
              <div className="px-6 py-3 border-b flex items-end justify-end gap-4">
                <div className="w-1/4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>

                  <select
                    name="approval"
                    value={selectedApprovalStatus}
                    onChange={submitStatusChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select</option>

                    {activeTab !== "draft" && (
                      <option value="draft">Draft</option>
                    )}

                    <option value="submitted">Submit for approval</option>
                  </select>
                </div>

                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleSubmitSelected}
                    disabled={selectedIds.length === 0 && selectedSprintIds.length === 0}
                    className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <FiCheckCircle className="mr-1" />
                    Submit
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-12 flex flex-col items-center justify-center text-center">
          <FiCalendar className="text-4xl text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No data found</p>
          <p className="text-gray-400 text-sm mt-1">
            There are no {TABS.find(t => t.key === activeTab)?.label.toLowerCase()} time entries to display.
          </p>
        </div>
      )
      }
    </div>
  );
};

export default TimeEntryList;